import React from "react";
import { Link } from "react-router-dom";
import {
  FaLock,
  FaFileContract,
  FaCreditCard,
  FaCalendarAlt,
  FaClock,
  FaUsers,
  FaTag,
  FaWineGlassAlt,
  FaCookie,
  FaWater,
  FaInfoCircle,
} from "react-icons/fa";
import BKTPayment from "./BKTPayment";

const PaymentCheckout = ({
  bookingData,
  onSuccess,
  onError,
  createBooking,
}) => {
  const hasExtras =
    bookingData.extras?.drinks?.length > 0 ||
    bookingData.extras?.snacks?.length > 0 ||
    bookingData.extras?.food?.length > 0 ||
    bookingData.extras?.jetSki;

  const extrasTotal =
    (bookingData.extras?.drinks?.reduce(
      (s, d) => s + d.price * d.quantity,
      0,
    ) || 0) +
    (bookingData.extras?.snacks?.reduce(
      (s, d) => s + d.price * d.quantity,
      0,
    ) || 0) +
    (bookingData.extras?.food?.reduce((s, d) => s + d.price * d.quantity, 0) ||
      0) +
    (bookingData.extras?.jetSki ? 50 : 0);

  const basePrice = bookingData.totalPrice - extrasTotal;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-5 sm:p-7 text-white">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-xl bg-sky-500/20 flex items-center justify-center">
            <FaCreditCard className="text-sky-400 text-sm" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold">Complete Payment</h2>
        </div>
        <p className="text-slate-400 text-sm ml-12">
          Secure payment powered by BKT Bank
        </p>
      </div>

      {/* Booking Summary */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
          <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
            <span className="w-1.5 h-4 bg-sky-500 rounded-full inline-block"></span>
            Booking Summary
          </h3>
        </div>
        <div className="divide-y divide-slate-100">
          <div className="flex justify-between items-center px-5 py-3">
            <span className="flex items-center gap-2 text-xs text-slate-500">
              <FaTag className="text-slate-300" />
              Service
            </span>
            <span className="text-xs font-semibold text-slate-900 text-right max-w-[55%]">
              {bookingData.serviceType}
            </span>
          </div>
          <div className="flex justify-between items-center px-5 py-3">
            <span className="flex items-center gap-2 text-xs text-slate-500">
              <FaCalendarAlt className="text-slate-300" />
              Date
            </span>
            <span className="text-xs font-semibold text-slate-900">
              {bookingData.date
                ? new Date(bookingData.date).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                : "Not selected"}
            </span>
          </div>
          {bookingData.timeSlot && (
            <div className="flex justify-between items-center px-5 py-3">
              <span className="flex items-center gap-2 text-xs text-slate-500">
                <FaClock className="text-slate-300" />
                Time
              </span>
              <span className="text-xs font-semibold text-slate-900">
                {bookingData.timeSlot}
              </span>
            </div>
          )}
          <div className="flex justify-between items-center px-5 py-3">
            <span className="flex items-center gap-2 text-xs text-slate-500">
              <FaUsers className="text-slate-300" />
              Guests
            </span>
            <span className="text-xs font-semibold text-slate-900">
              {bookingData.numberOfGuests}
            </span>
          </div>
          <div className="flex justify-between items-center px-5 py-3 bg-slate-50/50">
            <span className="text-xs text-slate-500">Base Price</span>
            <span className="text-xs font-semibold text-slate-900">
              €{basePrice.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Extras breakdown */}
        {hasExtras && (
          <div className="border-t border-slate-100">
            <div className="px-5 py-3 bg-teal-50 flex items-center gap-2">
              <span className="text-xs font-bold text-teal-700">
                Extras Added
              </span>
              <span className="bg-teal-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {(bookingData.extras?.drinks?.length || 0) +
                  (bookingData.extras?.snacks?.length || 0) +
                  (bookingData.extras?.food?.length || 0) +
                  (bookingData.extras?.jetSki ? 1 : 0)}{" "}
                item
                {(bookingData.extras?.drinks?.length || 0) +
                  (bookingData.extras?.snacks?.length || 0) +
                  (bookingData.extras?.food?.length || 0) +
                  (bookingData.extras?.jetSki ? 1 : 0) !==
                1
                  ? "s"
                  : ""}
              </span>
            </div>
            <div className="px-5 py-2 divide-y divide-slate-50">
              {bookingData.extras?.jetSki && (
                <div className="flex justify-between items-center py-2">
                  <span className="flex items-center gap-2 text-xs text-slate-600">
                    <span className="w-5 h-5 rounded-md bg-sky-100 flex items-center justify-center flex-shrink-0">
                      <FaWater className="text-sky-500 text-[9px]" />
                    </span>
                    Jet Ski on Board × 1
                  </span>
                  <span className="text-xs font-semibold text-sky-700">
                    €50.00
                  </span>
                </div>
              )}
              {bookingData.extras?.drinks?.map((drink, i) => (
                <div
                  key={`d-${i}`}
                  className="flex justify-between items-center py-2"
                >
                  <span className="flex items-center gap-2 text-xs text-slate-600">
                    <span className="w-5 h-5 rounded-md bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <FaWineGlassAlt className="text-blue-500 text-[9px]" />
                    </span>
                    {drink.name} × {drink.quantity}
                  </span>
                  <span className="text-xs font-semibold text-blue-700">
                    €{(drink.price * drink.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
              {bookingData.extras?.snacks?.map((snack, i) => (
                <div
                  key={`s-${i}`}
                  className="flex justify-between items-center py-2"
                >
                  <span className="flex items-center gap-2 text-xs text-slate-600">
                    <span className="w-5 h-5 rounded-md bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <FaCookie className="text-amber-500 text-[9px]" />
                    </span>
                    {snack.name} × {snack.quantity}
                  </span>
                  <span className="text-xs font-semibold text-amber-700">
                    €{(snack.price * snack.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
              {bookingData.extras?.food?.map((food, i) => (
                <div
                  key={`f-${i}`}
                  className="flex justify-between items-center py-2"
                >
                  <span className="text-xs text-slate-600">
                    {food.name} × {food.quantity}
                  </span>
                  <span className="text-xs font-semibold text-slate-700">
                    €{(food.price * food.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
            <div className="px-5 py-2.5 bg-teal-50/50 flex justify-between border-t border-teal-100">
              <span className="text-xs font-semibold text-teal-700">
                Extras Subtotal
              </span>
              <span className="text-xs font-bold text-teal-700">
                €{extrasTotal.toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* Grand total */}
        <div className="px-5 py-4 bg-gradient-to-r from-slate-800 to-slate-900 flex justify-between items-center">
          <span className="text-sm font-bold text-slate-300">Total Amount</span>
          <span className="text-2xl font-black text-white">
            €{bookingData.totalPrice.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Cancellation Policy */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
          <FaInfoCircle className="text-slate-400 text-xs" />
          <h3 className="font-bold text-slate-800 text-sm">
            Cancellation Policy
          </h3>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-emerald-600 text-[10px] font-bold">✓</span>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-800">
                Free Cancellation
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                Full refund if cancelled at least 48 hours before the activity
                start time.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-sky-600 text-[10px] font-bold">✓</span>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-800">
                Weather Exception
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                In case of unsafe weather conditions, you will receive a full
                refund or the option to reschedule to another available date at
                no extra cost.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* BKT Bank Virtual POS Payment */}
      <BKTPayment
        bookingData={bookingData}
        createBooking={createBooking}
        onSuccess={onSuccess}
        onError={onError}
      />

      {/* Policy Links */}
      <div className="flex flex-wrap items-center justify-center gap-4 pb-1">
        <Link
          to="/payment-policy"
          className="inline-flex items-center gap-1.5 text-slate-400 hover:text-slate-700 font-medium text-xs transition-colors"
        >
          <FaLock className="text-[9px]" />
          Payment Policy
        </Link>
        <Link
          to="/terms-and-conditions"
          className="inline-flex items-center gap-1.5 text-slate-400 hover:text-slate-700 font-medium text-xs transition-colors"
        >
          <FaFileContract className="text-[9px]" />
          Terms & Conditions
        </Link>
      </div>
    </div>
  );
};

export default PaymentCheckout;
